import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GroupIcon from '@mui/icons-material/Group';
import GroupsIcon from '@mui/icons-material/Groups';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SettingsIcon from '@mui/icons-material/Settings';
import SchoolIcon from '@mui/icons-material/School';
import { SIDEBAR_WIDTH, ROLES, BILLING_ADMIN } from '../../utils/constants';
import useAuth from '../../hooks/useAuth';

const navItems = [
  { label: 'Dashboard', path: '/', icon: <DashboardIcon />, roles: [ROLES.ADMIN, ROLES.FACULTY, ROLES.OBSERVER] },
  { label: 'Students', path: '/students', icon: <GroupIcon />, roles: [ROLES.ADMIN, ROLES.FACULTY] },
  { label: 'Cohorts', path: '/cohorts', icon: <GroupsIcon />, roles: [ROLES.ADMIN] },
  { label: 'Credit Tracker', path: '/credits', icon: <AssessmentIcon />, roles: [ROLES.ADMIN, ROLES.FACULTY] },
  { label: 'Reports', path: '/reports', icon: <DescriptionIcon />, roles: [ROLES.ADMIN, ROLES.OBSERVER] },
  // Billing is hidden unless the user holds the billing-admin flag — a regular
  // college_admin without the flag won't even see the entry.
  { label: 'Billing', path: '/billing', icon: <ReceiptLongIcon />, roles: [BILLING_ADMIN] },
  { label: 'Settings', path: '/settings', icon: <SettingsIcon />, roles: [ROLES.ADMIN] },
];

export default function Sidebar({ mobileOpen, onMobileClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const { user, role, isBillingAdmin } = useAuth();
  const visibleItems = navItems.filter((item) => {
    // Items keyed on the BILLING_ADMIN sentinel require the per-user flag,
    // not a role match — and they're hidden entirely when absent (no
    // grayed-out placeholder).
    if (item.roles.includes(BILLING_ADMIN)) return isBillingAdmin;
    return item.roles.includes(role);
  });

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Co-branding header: prominent college logo + name + portal eyebrow */}
      <Box sx={{
        px: 2.5,
        pt: 3,
        pb: 2,
        textAlign: 'center',
        background: 'linear-gradient(180deg, rgba(30,58,138,0.04) 0%, transparent 100%)',
      }}>
        <Avatar
          src={user?.college_logo_url || undefined}
          alt={user?.college_name || 'College'}
          variant="rounded"
          sx={{
            width: 80,
            height: 80,
            mx: 'auto',
            mb: 1.25,
            bgcolor: user?.college_logo_url ? 'background.paper' : 'primary.light',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            '& img': { objectFit: 'contain', p: 0.75 },
          }}
        >
          {!user?.college_logo_url && <SchoolIcon sx={{ fontSize: 40 }} />}
        </Avatar>
        {user?.college_name && (
          <Typography
            variant="subtitle2"
            sx={{ color: 'text.primary', fontWeight: 700, lineHeight: 1.25 }}
            title={user.college_name}
          >
            {user.college_name}
          </Typography>
        )}
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.65rem', fontWeight: 600 }}
        >
          College Portal
        </Typography>
      </Box>
      <Divider />
      <List sx={{ flex: 1, px: 1.5, py: 1 }}>
        {visibleItems.map((item) => {
          const isActive =
            item.path === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(item.path);

          return (
            <ListItemButton
              key={item.path}
              component={NavLink}
              to={item.path}
              onClick={isMobile ? onMobileClose : undefined}
              sx={{
                borderRadius: 1.5,
                mb: 0.5,
                backgroundColor: isActive ? 'primary.main' : 'transparent',
                color: isActive ? 'white' : 'text.primary',
                '&:hover': {
                  backgroundColor: isActive ? 'primary.dark' : 'action.hover',
                },
              }}
            >
              <ListItemIcon
                sx={{ color: isActive ? 'white' : 'text.secondary', minWidth: 40 }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontWeight: isActive ? 600 : 400, fontSize: '0.9rem' }}
              />
            </ListItemButton>
          );
        })}
      </List>
      <Box sx={{
        px: 2.5,
        py: 1.5,
        borderTop: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
      }}>
        <Box
          component="img"
          src="/images/cwc-logo-full.png"
          alt="Cyber Wise Corp"
          sx={{ height: 28, objectFit: 'contain', flexShrink: 0 }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1, fontSize: '0.65rem' }}>
            Powered by
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', display: 'block', lineHeight: 1.2 }}>
            Cyber Wise Corp
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{ '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH, boxSizing: 'border-box' } }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: SIDEBAR_WIDTH,
          boxSizing: 'border-box',
          borderRight: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}
